// ViewSection.tsx
'use client';

import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import type { PropsWithChildren } from 'react';
import { ChevronUp } from '@/components/ui/lucide-memo';
import { portalScrollableContent, portalViewWrapper } from '@/components/portal/portalTheme';
import { useKernelStore } from '@/kernel/kernelStores';
import { getSessionTableFocusLabel, resolvePortalViewFromTableFocus, selectActiveSubjectFromInventoryState } from '#lib/leasing/session-focus';

interface ViewSectionProps {
  wrapperClassName?: string;
  contentClassName?: string;
  onReachBottom?: () => void;
  hideViewLabel?: boolean;
  useAbsoluteScrollContainer?: boolean;
}

export const ViewSection = memo(({
  children,
  wrapperClassName,
  contentClassName,
  onReachBottom,
  hideViewLabel = false,
  useAbsoluteScrollContainer = true,
}: PropsWithChildren<ViewSectionProps>) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const setGlobalScrollTop = useKernelStore((state) => state.setGlobalScrollTop);
  const lang = useKernelStore((state) => state.language);
  const activeTableView = useKernelStore((state) =>
    resolvePortalViewFromTableFocus(selectActiveSubjectFromInventoryState(state))
  );

  // High-frequency scroll listener for backdrop only
  useEffect(() => {
    const node = scrollRef.current;
    if (!node) return;

    const handleScroll = () => {
      setGlobalScrollTop(node.scrollTop);
      setShowScrollTop(node.scrollTop > 100);
    };

    handleScroll();
    node.addEventListener('scroll', handleScroll, { passive: true });
    return () => node.removeEventListener('scroll', handleScroll);
  }, [setGlobalScrollTop]);

  // Performant bottom detection using IntersectionObserver
  useEffect(() => {
    if (!onReachBottom) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          onReachBottom();
        }
      },
      // R1: Si on n'utilise pas le scroll absolu (h-auto), le root DOIT être null (le viewport du navigateur)
      { root: useAbsoluteScrollContainer ? scrollRef.current : null, threshold: 0.1 }
    );

    const target = bottomRef.current;
    if (target) observer.observe(target);

    return () => {
      if (target) observer.unobserve(target);
    };
  }, [onReachBottom, useAbsoluteScrollContainer]);

  const handleScrollToTop = useCallback(() => {
    scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Isolate children from scrollTop re-renders
  const content = useMemo(() => {
    const viewLabel = activeTableView ? getSessionTableFocusLabel(activeTableView, lang) : '';
    const showViewLabel = !hideViewLabel && activeTableView;

    return (
      <div
        className={clsx(
          useAbsoluteScrollContainer
            ? `${portalScrollableContent} px-6 py-4 space-y-6 relative z-10`
            : 'px-6 py-4 space-y-6 relative z-10',
          contentClassName,
        )}
      >
        {showViewLabel && (
          <div className="flex flex-wrap items-center justify-between gap-4 min-h-[32px]">
            <div className="text-sm text-white/50 font-bold">
              {viewLabel}
            </div>
          </div>
        )}
        {children}
        {/* Sentinel for infinite scroll */}
        <div ref={bottomRef} className="h-4 w-full pointer-events-none" />
      </div>
    );
  }, [children, contentClassName, activeTableView, lang, hideViewLabel, useAbsoluteScrollContainer]);

  return (
    <div className={clsx('relative', portalViewWrapper, wrapperClassName)}>
      <div
        ref={scrollRef}
        className={clsx(
          'overflow-y-auto custom-scrollbar',
          useAbsoluteScrollContainer ? 'absolute inset-0' : 'relative'
        )}
      >
        {content}
      </div>
      {showScrollTop && (
        <button
          onClick={handleScrollToTop}
          className="absolute top-3 left-1/2 -translate-x-1/2 z-[100] h-8 w-8 rounded-full bg-[#2A2A2A] border border-white/10 flex items-center justify-center text-white/50 hover:text-white hover:bg-[#3A3A3A] transition-colors shadow-lg"
          aria-label="Scroll to top"
        >
          <ChevronUp size={14} />
        </button>
      )}
    </div>
  );
});

ViewSection.displayName = 'ViewSection';
